import { produce } from "immer";
import i18next from "i18next";
import { IPayloadAction } from "../../../shared/shared.interfaces";
import {
  EFieldStatus,
  IClientDocSubmissionDetailsResponse,
  IClientDocTaskHistoryEntry,
  IDocValidationDetailsImagesReducerInterface,
  IDocValidationDetailsReducerInterface,
  IDocValidationAdditionalImagesReducerInterface,
} from "./doc-validation-details.interface";
import { getDocSubmissionStatus } from "../ekyc.shared.utils";
import { ClientDocSubmissionStatusEnum, ClientDocSubmissionStatusEnumKeys } from "../ekyc.shared.enums";
import { IClientDocStatusHistoryEntry } from "./doc-status-history.model";
import { DocValidationDetailsUtils } from "./doc-validation-details.utils";
import { IRiskRatingHistoryEntry } from "./doc-risk-rating-history.model";

export const DocValidationDetailsActions = {
  types: {
    FETCH_DOC_DETAILS_SUCCESS: "FETCH_DOC_DETAILS_SUCCESS",
    FETCH_DOC_DETAILS_IMAGES_SUCCESS: "FETCH_DOC_DETAILS_IMAGES_SUCCESS",
    FETCH_DOC_ADDITIONAL_IMAGES_SUCCESS: "FETCH_DOC_ADDITIONAL_IMAGES_SUCCESS",
  },
  creators: {
    fetchDocDetailsSuccessAction: (detailsResponse: IClientDocSubmissionDetailsResponse) => ({
      type: DocValidationDetailsActions.types.FETCH_DOC_DETAILS_SUCCESS,
      payload: {
        detailsResponse,
      },
    }),
    fetchDocDetailsImagesSuccessAction: (imagesResponse: IDocValidationDetailsImagesReducerInterface[]) => ({
      type: DocValidationDetailsActions.types.FETCH_DOC_DETAILS_IMAGES_SUCCESS,
      payload: {
        imagesResponse,
      },
    }),
    fetchDocAdditionalImagesSuccessAction: (imagesResponse: IDocValidationAdditionalImagesReducerInterface[]) => ({
      type: DocValidationDetailsActions.types.FETCH_DOC_ADDITIONAL_IMAGES_SUCCESS,
      payload: {
        imagesResponse,
      },
    }),
  },
};

const validationDetailsState: IDocValidationDetailsReducerInterface = {
  remoteId: "",
  requestId: "",
  fullName: "",
  dateOfBirth: {
    status: EFieldStatus.REVIEW,
    value: 0,
  },
  documentType: "",
  idNumber: {
    status: EFieldStatus.REVIEW,
    value: 0,
  },
  nationality: {
    status: EFieldStatus.REVIEW,
    value: 0,
  },
  phoneNumber: "",
  requestTime: 0,
  status: getDocSubmissionStatus(ClientDocSubmissionStatusEnum.UNKNOWN) as ClientDocSubmissionStatusEnumKeys,
  reason: undefined,
  expirationDate: undefined,
  userLanguage: "",
  numberOfIdNumberDuplications: 0,
  numberOfPhoneNumberDuplications: 0,
  statusHistory: [],
  changeHistories: [],
  riskRatingHistory: [],
  operationType: "",
  duplicateFaces: null,
  duplicateDocuments: null,
  g2Error: "",
  agent: {
    firstName: "",
    middleName: "",
    lastName: "",
    operatorId: "",
    shortcode: NaN,
  },
  lastOperator: {
    name: "",
    lastUpdated: 0,
  },
  penultimateOperator: {
    name: "",
    lastUpdated: 0,
  },
  statusCode: "",
};

const validationDetailsImagesState: IDocValidationDetailsImagesReducerInterface[] = [];
const validationAdditionalImagesState: IDocValidationAdditionalImagesReducerInterface[] = [];

export const docValidationDetailsReducer = produce(
  (draft: IDocValidationDetailsReducerInterface, action: IPayloadAction<any>) => {
    switch (action.type) {
      case DocValidationDetailsActions.types.FETCH_DOC_DETAILS_SUCCESS:
        const detailsResponse: IClientDocSubmissionDetailsResponse = action.payload.detailsResponse;
        draft.remoteId = detailsResponse.id;
        draft.requestId = detailsResponse.requestId;
        draft.fullName = `${detailsResponse.firstName.value} ${detailsResponse.lastName.value}`;
        draft.firstName = detailsResponse.firstName;
        draft.lastName = detailsResponse.lastName;
        draft.dateOfBirth = detailsResponse.dateOfBirth;
        draft.placeOfBirth = detailsResponse.placeOfBirth;
        draft.gender = detailsResponse.gender || { status: EFieldStatus.REVIEW, value: "unknownValue" };
        draft.documentType = detailsResponse.documentType || "unknownValue";
        draft.idNumber = detailsResponse.documentNumber;
        draft.nationality = detailsResponse.nationality
          ? {
              ...detailsResponse.nationality,
              value: i18next.exists(`market_values:nationalities.${detailsResponse.nationality.value}`)
                ? String(i18next.t(`market_values:nationalities.${detailsResponse.nationality.value}`))
                : String(i18next.t("commons.unknownValue")),
            }
          : {
              status: EFieldStatus.REVIEW,
              value: String(i18next.t("commons.unknownValue")),
            };
        draft.occupation = detailsResponse.occupation;
        draft.income = detailsResponse.income;
        draft.riskRating = detailsResponse.riskRating;
        draft.idCardIssuingBy = detailsResponse.idCardIssuingBy;
        draft.idCardIssuingDate = detailsResponse.idCardIssuingDate;
        draft.idCardIssuingPlace = detailsResponse.idCardIssuingPlace;
        draft.addressCommune = detailsResponse.commune;
        draft.addressStreet = detailsResponse.street;
        draft.addressProvince = detailsResponse.province;
        draft.addressTown = detailsResponse.town;
        draft.phoneNumber = detailsResponse.msisdn;
        draft.requestTime = detailsResponse.creationDate;
        draft.status = getDocSubmissionStatus(detailsResponse.status) as ClientDocSubmissionStatusEnumKeys;
        draft.statusCode = detailsResponse.status;
        draft.lastStatusTime = detailsResponse.updateStatusDate;
        draft.reason = detailsResponse.comment || "-";
        draft.expirationDate = detailsResponse.expirationDate;
        draft.userLanguage = detailsResponse.language;
        draft.numberOfIdNumberDuplications =
          detailsResponse.numberOfIdNumberDuplications > 0 ? detailsResponse.numberOfIdNumberDuplications : 0;
        draft.numberOfPhoneNumberDuplications =
          detailsResponse.numberOfPhoneNumberDuplications > 0 ? detailsResponse.numberOfPhoneNumberDuplications : 0;
        draft.lastSeenBy = detailsResponse.lastGetUser;
        draft.lastSeenTime = detailsResponse.lastGetTime;
        draft.penultSeenBy = detailsResponse.penultGetUser;
        draft.penultSeenTime = detailsResponse.penultGetTime;
        draft.whiteListedUser = detailsResponse.whiteListedUser;
        draft.g2Error = detailsResponse.g2ErrorComment || "";
        draft.operationType = detailsResponse.operationType || "";
        draft.lastOperator = {
          name: detailsResponse?.lastOperator?.name || "",
          lastUpdated: detailsResponse?.lastOperator?.lastUpdated ?? 0,
        };
        draft.penultimateOperator = {
          name: detailsResponse?.penultimateOperator?.name || "",
          lastUpdated: detailsResponse?.penultimateOperator?.lastUpdated ?? 0,
        };
        draft.agent = {
          operatorId: detailsResponse?.agent?.agentOperatorId || "-",
          shortcode: detailsResponse?.agent?.agentShortCode || "-",
          ...DocValidationDetailsUtils.splitName(detailsResponse?.agent?.agentName),
        };

        if (detailsResponse.registrationHistories) {
          draft.statusHistory = [];
          for (let i = 0; i < detailsResponse.registrationHistories.length; i++) {
            const entry = detailsResponse.registrationHistories[i];
            const newEntry: IClientDocStatusHistoryEntry = {
              username: entry.operator || "-",
              status: getDocSubmissionStatus(entry.status || "") as ClientDocSubmissionStatusEnumKeys,
              statusDate: entry.statusDate || 0,
            };

            draft.statusHistory.push(newEntry);
          }

          draft.statusHistory.sort((a, b) => {
            let result;
            if (a.statusDate === b.statusDate) {
              result = 0;
            } else {
              result = a.statusDate < b.statusDate ? 1 : -1;
            }
            return result;
          });
        }
        if (detailsResponse.changeHistories) {
          draft.changeHistories = [];
          for (let i = 0; i < detailsResponse.changeHistories.length; i++) {
            const entry = detailsResponse.changeHistories[i];
            if (entry.changeHistoryData?.length) {
              const changesArray = entry.changeHistoryData.map(change => ({
                field: change.field,
                oldValue: change.oldValue,
                newValue: change.newValue,
              }));
              const newEntry: IClientDocTaskHistoryEntry = {
                field: changesArray.length === 1 ? changesArray[0].field : undefined,
                oldValue: changesArray.length === 1 ? changesArray[0].oldValue : undefined,
                newValue: changesArray.length === 1 ? changesArray[0].newValue : undefined,
                status: entry.status,
                statusDate: entry.statusDate ?? Date.now(),
                statusReason: entry.statusReason ?? "-",
                comment: entry.comment ?? "-",
                approver: entry.approver ?? "-",
                operator: entry.operator ?? "-",
                dateCreated: entry.dateCreated,
                dateUpdated: entry.dateUpdated,
                changes: changesArray,
              };
              draft.changeHistories.push(newEntry);
            }
          }
          draft.changeHistories.sort((a, b) => (b.statusDate ?? 0) - (a.statusDate ?? 0));
        }
        if (detailsResponse.riskRatingHistory) {
          draft.riskRatingHistory = [];
          for (let i = 0; i < detailsResponse.riskRatingHistory.length; i++) {
            const entry = detailsResponse.riskRatingHistory[i];
            const newEntry: IRiskRatingHistoryEntry = {
              operator: entry.operator,
              riskRating: entry.riskRating,
              updatedAt: entry.updatedAt,
            };

            draft.riskRatingHistory.push(newEntry);
          }
        }

        draft.duplicateFaces = detailsResponse.duplicateFaces || null;

        draft.duplicateDocuments = detailsResponse.duplicateDocuments || null;
        draft.g2Status = detailsResponse?.g2Status;

        return draft;
      default:
        return draft;
    }
  },
  validationDetailsState,
);

export const docValidationDetailsImagesReducer = produce(
  (draft: IDocValidationDetailsImagesReducerInterface[], action: IPayloadAction<any>) => {
    switch (action.type) {
      case DocValidationDetailsActions.types.FETCH_DOC_DETAILS_IMAGES_SUCCESS:
        draft = action.payload.imagesResponse;
        return draft;
      default:
        return draft;
    }
  },
  validationDetailsImagesState,
);

export const docValidationAdditionalImagesReducer = produce(
  (draft: IDocValidationAdditionalImagesReducerInterface[], action: IPayloadAction<any>) => {
    switch (action.type) {
      case DocValidationDetailsActions.types.FETCH_DOC_ADDITIONAL_IMAGES_SUCCESS:
        draft.splice(0, draft.length, ...action.payload.imagesResponse);
        return draft;
      default:
        return draft;
    }
  },
  validationAdditionalImagesState,
);
